// ApiService.ts

import { toast } from 'react-toastify';
import { ChatAiModelService } from './ChatAiModelService';
import { IChatAiModelService } from './iChatAiModelService';
import { SemanticModel } from './dto/semanticModel';
import {
  ChatAiCompletionRequest,
  ChatAiReponseMessage,
} from './dto/completionRequest';
import { ToolCallRequest } from './dto/tools';
import { FeedbackPayload, FeedbackResponse } from './dto/feedback';

const HOST = process.env[`REACT_APP_API_URL`];
console.log('Chat MCP API URL: ', HOST);

/****************************
 * Types
 ***************************/
export interface CheckCompleteResult {
  shouldContinue: boolean;
  message: string | null;
}

/****************************
 * Helpers
 ***************************/
const handleApiError = (error: any, defaultMessage: string) => {
  console.error('API Error:', error);

  if (error?.trace_id) {
    const errorMessage = `${error.message}\n\nTrace ID: ${error.trace_id}`;
    toast.error(errorMessage);
  } else {
    toast.error(defaultMessage);
  }

  throw error;
};

/****************************
 * Service
 ***************************/
export const ApiService = {
  /**
   * Return a typed ChatAi service instance.
   */
  getChatAiService(accessToken?: string): IChatAiModelService {
    if (!accessToken) throw new Error('Access token is missing');
    return new ChatAiModelService(HOST!, accessToken);
  },

  /**
   * Fetch list of semantic model yaml files.
   */
  async fetchFileNames(
    accessToken: string | undefined,
    accountName: string
  ): Promise<SemanticModel[]> {
    return this.getChatAiService(accessToken).getSemanticModels(accountName);
  },

  /**
   * Poll completion endpoint and interpret control messages.
   */
  async checkComplete(
    accessToken: string | undefined,
    prompt: string,
    fileName: string,
    promptId: string,
    accountName: string
  ): Promise<CheckCompleteResult> {
    if (!accessToken) throw new Error('Access token is missing');

    const response = await fetch(`${HOST}/complete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ prompt, fileName, promptId, accountName }),
      credentials: 'include',
    });

    if (!response.ok) throw new Error('Failed to check completion status');

    const result = await response.json();
    const data = result.data || result;

    const defaultReturn: CheckCompleteResult = {
      shouldContinue: false,
      message: null,
    };

    if (
      !Array.isArray(data) ||
      data.length === 0 ||
      typeof data[0][0] !== 'string'
    ) {
      return defaultReturn;
    }

    const msg: string = data[0][0];

    if (msg.includes('FETCH THE DATA')) {
      const reason = msg
        .replace('FETCH THE DATA', 'Trying to get the SQL from Semantic Model:')
        .trim();
      return { shouldContinue: true, message: reason };
    }

    if (msg === 'FETCH THE DATA') {
      return { shouldContinue: true, message: null };
    }

    return { shouldContinue: false, message: msg };
  },

  /**
   * Submit user prompt for completion.
   */
  async submitPrompt(
    accessToken: string | undefined,
    prompt: string,
    chatAiModelId: string,
    isCompleteEnabled: boolean,
    prompt_id: any,
    isOnlySQLEnabled: boolean
  ): Promise<any> {
    if (!accessToken) throw new Error('Access token is missing');

    try {
      const request: ChatAiCompletionRequest = {
        model: chatAiModelId,
        store: true,
        completion_id: chatAiModelId,
        request_id: prompt_id,
        tool_choice: isOnlySQLEnabled ? null : 'auto',
        messages: [
          {
            role: 'user',
            content: prompt,
          },
        ],
        max_tokens: 150,
      };

      const response =
        await this.getChatAiService(accessToken).chatCompletion(request);

      const msg = response.choices[0].message;
      const sqlResult = msg.tool_calls?.[0]?.result ?? [];
      const unescaped = JSON.parse(msg.content);
      const message: ChatAiReponseMessage = unescaped;

      return {
        sql_result: sqlResult,
        message,
        request_id: response.request_id,
      };
    } catch (error) {
      handleApiError(
        error,
        'An error occurred while submitting the prompt. Please try again.'
      );
    }
  },

  /**
   * Submit chat feedback (thumbs up/down) for a prompt.
   */
  async submitChatFeedback(
    accessToken: string | undefined,
    payload: FeedbackPayload
  ): Promise<FeedbackResponse> {
    if (!accessToken) throw new Error('Access token is missing');
    try {
      const response =
        await this.getChatAiService(accessToken).submitChatFeedback(payload);
      return response;
    } catch (error) {
      handleApiError(error, 'An error occurred while submitting feedback.');
      throw error;
    }
  },

  /**
   * Execute SQL tool call.
   */
  async executeSQL(
    accessToken: string | undefined,
    sql_query: string,
    accountName: string,
    fileName: string
  ): Promise<{ sql_result: Record<string, string>[]; metadata?: any }> {
    try {
      const request: ToolCallRequest = {
        tool: 'snowflake_query',
        parameters: {
          query: sql_query,
          accountName,
          model: fileName,
        },
      };

      const response =
        await this.getChatAiService(accessToken).executeTool(request);
      return {
        sql_result: response.data, // API returns 'data', not 'sql_result'
        metadata: response.metadata
      };
    } catch (error) {
      handleApiError(
        error,
        'An error occurred while executing SQL. Please try again.'
      );
      throw error;
    }
  },

  /**
   * Fetch previous chat prompts for a model.
   */
  async fetchChatHistory(
    accessToken: string | undefined,
    fileName: string
  ): Promise<any> {
    if (!accessToken) throw new Error('Access token is missing');

    try {
      const chatIdModel =
        await this.getChatAiService(accessToken).getSemanticModel(fileName);
      return chatIdModel.prompts ?? [];
    } catch (error) {
      handleApiError(error, 'Failed to fetch chat history');
      throw error;
    }
  },

  /**
   * Get prompt record by ID.
   */
  async getByPromptId(
    accessToken: string | undefined,
    promptId: string
  ): Promise<any> {
    if (!accessToken) throw new Error('Access token is missing');

    try {
      const response = await fetch(`${HOST}/prompt_id?prompt_id=${promptId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const errorMessage = await response.text();
        throw new Error(errorMessage || 'Unknown error');
      }

      const result = await response.json();
      return result.data || result;
    } catch (error) {
      handleApiError(error, 'Failed to fetch prompt by id');
      throw error;
    }
  },

  /**
   * Execute completion to run the generated SQL.
   */
  async executeCompletion(
    requestId: string,
    accessToken: string,
    chatAiModelId: string
  ): Promise<{ sql_result: any[]; metadata?: any; trace_id: string; correlation_id?: string }> {
    if (!accessToken) throw new Error('Access token is missing');

    const request: ChatAiCompletionRequest = {
      model: chatAiModelId,
      store: true,
      completion_id: chatAiModelId,
      request_id: requestId,
      tool_choice: 'auto',
      messages: [{ role: 'user', content: 'Dummy' }],
      max_tokens: 150,
    };

    try {
      const result =
        await this.getChatAiService(accessToken).executeCompletion(request);
      return {
        sql_result: result.data,
        metadata: result.metadata, // Include metadata from API response
        trace_id: result.trace_id,
        correlation_id: result.correlation_id,
      };
    } catch (error) {
      handleApiError(
        error,
        'An error occurred while executing the completion.'
      );
      throw error;
    }
  },
};
